import type {
    ComponentDescription,
    PluginConfig,
    PropertyDescription,
    TabDescription,
    ScreenDescription,
    AppDescription,
    NonUserAppFeatures,
} from "@glide/app-description";
import { getScreenProperty, ScreenDescriptionKind } from "@glide/app-description";
import { removeTab } from "./app-action-utils";
import { getAppTabs, getSourceMetadata } from "@glide/common-core/dist/js/components/SerializedApp";
import { assertNever, defined, mapFilterUndefined, proveNever } from "@glideapps/ts-necessities";
import { handleReorderComponent } from "./app-action-reorder-components";
import { getScreenComponents, isClassOrArrayScreenDescription } from "@glide/common-core";
import { nonNull, replaceArrayItem, deepEqual } from "@glide/support";
import type { GoogleSheetSourceMetadata, SourceMetadata } from "@glide/type-schema";
import { areSourceMetadatasEqual } from "@glide/type-schema";
import { findComponentIndexByID, getSubComponents } from "@glide/generator/dist/js/sub-components";
import type { ComponentIndexes } from "@glide/common-core/dist/js/component-indexes";
import type { BaseTheme } from "../../../base-theme/dist/ts";
import { ErrorInfo, isErrorInfo, type ErrorOr } from "@glide/error-info";

export enum AppMutationKind {
    UpdateTabDescription = "UPDATE_TAB_DESCRIPTION",
    UpsertSourceMetadata = "ADD_SOURCE_METADATA",
    UpsertGoogleSheetSourceMetadata = "ADD_OR_REPLACE_GOOGLE_SHEET_SOURCE_METADATA",
    RemoveTab = "REMOVE_TAB",
    AddTab = "ADD_TAB",
    UpdateSerializedApp = "UPDATE_SERIALIZED_APP",
    ReorderTabs = "REORDER_TABS",
    UpsertPluginConfig = "UPSERT_PLUGIN_CONFIG",
    DeletePluginConfig = "DELETE_PLUGIN_CONFIG",
    UpdatePluginConfigParameters = "UPDATE_PLUGIN_CONFIG_PARAMETERS",
    RemoveSourceMetadata = "REMOVE_NATIVE_TABLE_SOURCE_METADATA",
    UpdateComponentDescription = "UPDATE_COMPONENT_DESCRIPTION",
    AddTabTemplate = "ADD_TAB_TEMPLATE",
    RemoveTabTemplate = "REMOVE_TAB_TEMPLATE",
    UpdateScreenDescription = "UPDATE_SCREEN_DESCRIPTION",
    ReorderComponent = "REORDER_COMPONENT",
    ApplyUndo = "APPLY_UNDO",
    UpdateTheme = "UPDATE_THEME",
    UpdateFeatures = "UPDATE_FEATURES",
}

export interface UpdateTabDescriptionData {
    readonly screenName: string;
    readonly desc: TabDescription;
    // Only change it if it's equal to this
    readonly before?: TabDescription;
}

export interface UpsertSourceMetadataData {
    readonly sourceMetadata: SourceMetadata;
}

export interface UpsertGoogleSheetSourceMetadataData {
    readonly sourceMetadata: GoogleSheetSourceMetadata;
}

export interface RemoveTabData {
    readonly screenName: string;
    // Only delete it if it's equal to this
    readonly before?: TabDescription;
}

export interface AddTabData {
    readonly tabDescription: TabDescription;
    readonly index: number;
}

export interface UpdateSerializedAppData {
    readonly updates: Partial<AppDescription>;
    readonly before?: Partial<AppDescription>;
}

export interface ReorderTabsData {
    readonly screenNames: readonly string[];
    readonly before?: readonly string[];
}

export interface UpsertPluginConfigData {
    readonly pluginConfig: PluginConfig;
    // `null` means it didn't exist before
    readonly before?: PluginConfig | null;
}

export interface DeletePluginConfigData {
    readonly pluginConfig: Pick<PluginConfig, "pluginID" | "configID">;
    readonly before?: PluginConfig;
}

export interface UpdatePluginConfigParametersData {
    readonly pluginConfigID: string;
    readonly pluginParams: Record<string, PropertyDescription>;
    readonly before?: Record<string, PropertyDescription>;
}

export interface ReorderComponentData {
    readonly screenName: string;
    readonly fromComponentID: string;
    readonly to: ComponentIndexes;
}

export interface RemoveSourceMetadataData {
    readonly sourceMetadata: SourceMetadata;
}

export interface UpdateComponentDescriptionData {
    readonly screenName: string;
    readonly componentID: string;
    readonly newDescription: ComponentDescription;
    readonly before?: ComponentDescription;
}

export interface AddTabTemplateData {
    readonly tabsToAdd: readonly TabDescription[];
    readonly screensToAdd: Record<string, ScreenDescription>;
}

export interface RemoveTabTemplateData {
    readonly tabsToRemove: readonly TabDescription[];
    readonly screensToRemove: Record<string, ScreenDescription>;
}

export interface UpdateScreenDescriptionData {
    readonly screenName: string;
    /** `false` means delete the screen */
    readonly updates: Partial<ScreenDescription> | false;
    readonly allowNewScreen: boolean;
    // `null` means it didn't exist before
    readonly before?: Partial<ScreenDescription> | null;
}

export interface ApplyUndoData {
    readonly appDescription: AppDescription;
}

export interface UpdateThemeData {
    readonly updates: Partial<BaseTheme>;
    readonly before?: Partial<BaseTheme>;
}

export interface UpdateFeaturesData {
    readonly updates: Partial<NonUserAppFeatures>;
    readonly before?: Partial<NonUserAppFeatures>;
}

interface MutType<TKind extends AppMutationKind, TData> {
    readonly kind: TKind;
    readonly data: TData;
}

type UpdateTabDescriptionMutation = MutType<AppMutationKind.UpdateTabDescription, UpdateTabDescriptionData>;
type AddSourceMetadataMutation = MutType<AppMutationKind.UpsertSourceMetadata, UpsertSourceMetadataData>;
type AddOrReplaceGoogleSheetSourceMetadataMutation = MutType<
    AppMutationKind.UpsertGoogleSheetSourceMetadata,
    UpsertGoogleSheetSourceMetadataData
>;
type RemoveTabMutation = MutType<AppMutationKind.RemoveTab, RemoveTabData>;
type AddTabMutation = MutType<AppMutationKind.AddTab, AddTabData>;
type UpdateSerializedAppMutation = MutType<AppMutationKind.UpdateSerializedApp, UpdateSerializedAppData>;
type ReorderTabsMutation = MutType<AppMutationKind.ReorderTabs, ReorderTabsData>;
type UpsertPluginConfigMutation = MutType<AppMutationKind.UpsertPluginConfig, UpsertPluginConfigData>;
type DeletePluginConfigMutation = MutType<AppMutationKind.DeletePluginConfig, DeletePluginConfigData>;
type UpdatePluginConfigParametersMutation = MutType<
    AppMutationKind.UpdatePluginConfigParameters,
    UpdatePluginConfigParametersData
>;
type ReorderComponentMutation = MutType<AppMutationKind.ReorderComponent, ReorderComponentData>;
type RemoveSourceMetadataMutation = MutType<AppMutationKind.RemoveSourceMetadata, RemoveSourceMetadataData>;
type UpdateComponentDescriptionMutation = MutType<
    AppMutationKind.UpdateComponentDescription,
    UpdateComponentDescriptionData
>;
type AddTabTemplateMutation = MutType<AppMutationKind.AddTabTemplate, AddTabTemplateData>;
type RemoveTabTemplateMutation = MutType<AppMutationKind.RemoveTabTemplate, RemoveTabTemplateData>;
type UpdateScreenDescriptionMutation = MutType<AppMutationKind.UpdateScreenDescription, UpdateScreenDescriptionData>;
type ApplyUndoMutation = MutType<AppMutationKind.ApplyUndo, ApplyUndoData>;
type UpdateThemeMutation = MutType<AppMutationKind.UpdateTheme, UpdateThemeData>;
type UpdateFeaturesMutation = MutType<AppMutationKind.UpdateFeatures, UpdateFeaturesData>;

export type AppDescriptionMutation =
    | UpdateTabDescriptionMutation
    | AddSourceMetadataMutation
    | AddOrReplaceGoogleSheetSourceMetadataMutation
    | RemoveTabMutation
    | AddTabMutation
    | UpdateSerializedAppMutation
    | ReorderTabsMutation
    | UpsertPluginConfigMutation
    | DeletePluginConfigMutation
    | UpdatePluginConfigParametersMutation
    | RemoveSourceMetadataMutation
    | UpdateComponentDescriptionMutation
    | AddTabTemplateMutation
    | RemoveTabTemplateMutation
    | UpdateScreenDescriptionMutation
    | ReorderComponentMutation
    | ApplyUndoMutation
    | UpdateThemeMutation
    | UpdateFeaturesMutation;

function updateScreenDescription(
    appDesc: AppDescription,
    screenName: string,
    cb: (oldScreenDesc: ScreenDescription | undefined) => ScreenDescription | undefined
): AppDescription {
    const screenDesc = appDesc.screenDescriptions[screenName];

    const newScreenDescription = cb(screenDesc);
    let screenDescriptions: Record<string, ScreenDescription>;
    if (newScreenDescription !== undefined) {
        screenDescriptions = {
            ...appDesc.screenDescriptions,
            [screenName]: newScreenDescription,
        };
    } else {
        screenDescriptions = { ...appDesc.screenDescriptions };
        delete screenDescriptions[screenName];
    }

    return {
        ...appDesc,
        screenDescriptions,
    };
}

/**
 * Returns `undefined` if the update is a no-op.  `antiUpdates` and
 * `antiBefore` are the `updates` and `before` for the anti-mutation.
 */
function updateShallow<T extends Record<string, unknown>>(
    old: T,
    before: Partial<T> | undefined,
    updates: Partial<T>
): ErrorOr<{ updated: T; antiUpdates: Partial<T>; antiBefore: Partial<T> } | undefined> {
    const updated = { ...old };
    const antiUpdates: Partial<T> = {};
    const antiBefore: Partial<T> = {};
    let changed = false;

    for (const [k, v] of Object.entries(updates)) {
        if (deepEqual(old[k], v)) continue;

        if (before !== undefined) {
            if (!deepEqual(old[k], before[k])) return new ErrorInfo(412, "Value has changed");
        }

        (antiUpdates as any)[k] = old[k];
        (antiBefore as any)[k] = v;
        if (v !== undefined) {
            (updated as any)[k] = v;
        } else {
            delete (updated as any)[k];
        }

        changed = true;
    }

    if (!changed) return undefined;

    return { updated, antiUpdates, antiBefore };
}

type MutationResult = {
    newState: AppDescription;
    /** This is the same as `mutation`, but conditioned on the current
     * state of the app. */
    conditionalMutation: AppDescriptionMutation;
    /** If there is no anti-mutation, then this mutation cannot be
     * undone. */
    antiMutation: AppDescriptionMutation | undefined;
};

function applyUpdateTabDescription(
    oldDesc: AppDescription,
    mutation: UpdateTabDescriptionMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screenName, desc, before: beforeDesc } = mutation.data;

    const newScreenName = getScreenProperty(desc.screenName);
    if (newScreenName === undefined) return new ErrorInfo(400, "No screen associated with tab");

    const tabs = getAppTabs(oldDesc);
    const index = tabs.findIndex(t => getScreenProperty(t.screenName) === screenName);
    if (index < 0) return new ErrorInfo(404, "Tab not found");

    const oldTab = defined(tabs[index]);
    if (deepEqual(oldTab, desc)) return undefined;

    if (respectCondition && beforeDesc !== undefined && !deepEqual(oldTab, beforeDesc)) {
        return new ErrorInfo(412, "Tab has changed");
    }

    const newTabs = replaceArrayItem(tabs, index, desc);

    return {
        newState: {
            ...oldDesc,
            tabs: newTabs,
        },
        conditionalMutation: {
            kind: AppMutationKind.UpdateTabDescription,
            data: {
                ...mutation.data,
                before: oldTab,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdateTabDescription,
            data: {
                screenName: newScreenName,
                desc: oldTab,
                before: desc,
            },
        },
    };
}

function applyRemoveTab(
    oldDesc: AppDescription,
    mutation: RemoveTabMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const result = removeTab(oldDesc, mutation.data.screenName, respectCondition ? mutation.data.before : undefined);
    if (isErrorInfo(result) || result === undefined) return result;
    const { newAppDescription, removedTab, index } = result;
    return {
        newState: newAppDescription,
        conditionalMutation: {
            kind: AppMutationKind.RemoveTab,
            data: {
                ...mutation.data,
                before: removedTab,
            },
        },
        antiMutation: {
            kind: AppMutationKind.AddTab,
            data: {
                tabDescription: removedTab,
                index,
            },
        },
    };
}

function applyReorderComponent(
    oldDesc: AppDescription,
    mutation: ReorderComponentMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screenName, fromComponentID, to } = mutation.data;
    const screenDesc = oldDesc.screenDescriptions[screenName];
    if (screenDesc === undefined) return new ErrorInfo(404, "Screen doesn't exist");

    const fromIndexes = findComponentIndexByID(screenDesc, fromComponentID);
    if (fromIndexes === undefined) return new ErrorInfo(404, "Component not found in screen");

    // Check if already at target position
    if (deepEqual(fromIndexes, to)) {
        return undefined; // No-op
    }

    return {
        newState: updateScreenDescription(
            oldDesc,
            mutation.data.screenName,
            old => handleReorderComponent(old, fromIndexes, to) ?? old
        ),
        conditionalMutation: mutation,
        antiMutation: {
            kind: AppMutationKind.ReorderComponent,
            data: {
                screenName,
                fromComponentID,
                to: fromIndexes,
            },
        },
    };
}

function applyAddTab(
    oldDesc: AppDescription,
    mutation: AddTabMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { tabDescription, index } = mutation.data;

    const screenName = getScreenProperty(tabDescription.screenName);
    if (screenName === undefined) return new ErrorInfo(400, "New tab has no screen");

    const screen = oldDesc.screenDescriptions[screenName];
    if (screen === undefined) return new ErrorInfo(404, "Screen for new tab doesn't exist");

    if (screen.kind === ScreenDescriptionKind.Class) {
        if (screen.fetchesData !== true) return new ErrorInfo(400, "Screen cannot be used for new tab");
    } else {
        if (
            screen.kind !== ScreenDescriptionKind.Array &&
            screen.kind !== ScreenDescriptionKind.Chat &&
            screen.kind !== ScreenDescriptionKind.ShoppingCart
        ) {
            return proveNever(
                screen,
                `Unknown screen kind ${(screen as any).kind}`,
                new ErrorInfo(500, "Screen for new tab is invalid")
            );
        }
    }

    const oldTabs = getAppTabs(oldDesc);
    const existingTab = oldTabs.find(t => getScreenProperty(t.screenName) === screenName);
    if (existingTab !== undefined && deepEqual(existingTab, tabDescription)) return undefined;

    const newTabs = [...oldTabs.slice(0, index), tabDescription, ...oldTabs.slice(index)];

    return {
        newState: { ...oldDesc, tabs: newTabs },
        conditionalMutation: mutation,
        antiMutation: {
            kind: AppMutationKind.RemoveTab,
            data: {
                screenName,
                before: tabDescription,
            },
        },
    };
}

function applyUpdateSerializedApp(
    oldDesc: AppDescription,
    mutation: UpdateSerializedAppMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { updates, before } = mutation.data;

    const result = updateShallow(oldDesc, respectCondition ? before : undefined, updates);
    if (isErrorInfo(result) || result === undefined) return result;
    const { updated, antiBefore, antiUpdates } = result;

    return {
        newState: updated,
        conditionalMutation: {
            kind: AppMutationKind.UpdateSerializedApp,
            data: {
                ...mutation.data,
                before: antiUpdates,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdateSerializedApp,
            data: {
                updates: antiUpdates,
                before: antiBefore,
            },
        },
    };
}

function applyUpsertPluginConfig(
    oldDesc: AppDescription,
    mutation: UpsertPluginConfigMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    // This is copied from `upsertPluginConfig` in `actions.ts`.  We
    // can remove it there when we get rid of the
    // `useSmallReduxActions` feature setting.
    const { pluginConfig: newConfig, before } = mutation.data;

    const pluginConfigs = oldDesc.pluginConfigs ?? [];

    let oldConfig: PluginConfig | undefined;
    if (newConfig.configID !== undefined) {
        // Find the existing config by config ID.
        oldConfig = pluginConfigs.find(p => p.configID === newConfig.configID);
    }
    if (oldConfig === undefined) {
        // If we don't have a config ID match, we find an existing config
        // with the same plugin ID that doesn't have a config ID.
        oldConfig = pluginConfigs.find(p => p.configID === undefined && p.pluginID === newConfig.pluginID);
    }

    if (oldConfig !== undefined && deepEqual(oldConfig, newConfig)) return undefined;

    let newPluginConfigs: PluginConfig[];
    if (oldConfig !== undefined) {
        // If there is an existing config, we replace it in place to maintain order
        const oldIndex = pluginConfigs.indexOf(oldConfig);
        newPluginConfigs = replaceArrayItem(pluginConfigs, oldIndex, newConfig);
    } else {
        // If it's a new config, append it
        newPluginConfigs = [...pluginConfigs, newConfig];
    }

    if (respectCondition && before !== undefined) {
        if (before === null) {
            if (oldConfig !== undefined) return new ErrorInfo(412, "Plugin config has changed");
        } else {
            if (!deepEqual(oldConfig, before)) return new ErrorInfo(412, "Plugin config has changed");
        }
    }

    let antiMutation: AppDescriptionMutation | undefined;
    if (oldConfig !== undefined) {
        antiMutation = {
            kind: AppMutationKind.UpsertPluginConfig,
            data: { pluginConfig: oldConfig, before: newConfig },
        };
    } else {
        antiMutation = {
            kind: AppMutationKind.DeletePluginConfig,
            data: { pluginConfig: newConfig, before: newConfig },
        };
    }

    return {
        newState: {
            ...oldDesc,
            pluginConfigs: newPluginConfigs,
        },
        conditionalMutation: {
            kind: AppMutationKind.UpsertPluginConfig,
            data: {
                ...mutation.data,
                before: oldConfig ?? null,
            },
        },
        antiMutation,
    };
}

function applyDeletePluginConfig(
    oldDesc: AppDescription,
    mutation: DeletePluginConfigMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    // This is copied from `deletePluginConfig` in `actions.ts`.  We
    // can remove it there when we get rid of the
    // `useSmallReduxActions` feature setting.
    const { pluginConfig, before } = mutation.data;
    const pluginConfigs = oldDesc.pluginConfigs ?? [];
    const oldConfigs = pluginConfigs.filter(p => {
        if (pluginConfig.configID !== undefined) {
            return p.configID === pluginConfig.configID;
        } else if (p.configID === undefined) {
            // If we don't have a config ID then we have to match
            // by plugin ID if `p` also doesn't have a config ID.
            return p.pluginID === pluginConfig.pluginID;
        } else {
            return false;
        }
    });
    const oldConfig = oldConfigs[0];
    if (oldConfig === undefined) {
        // Deleting a non-existent config is a no-op
        return undefined;
    }
    if (respectCondition && before !== undefined && !deepEqual(oldConfig, before)) {
        return new ErrorInfo(412, "Plugin config has changed");
    }
    return {
        newState: {
            ...oldDesc,
            pluginConfigs: pluginConfigs.filter(p => !oldConfigs.includes(p)),
        },
        conditionalMutation: {
            kind: AppMutationKind.DeletePluginConfig,
            data: {
                ...mutation.data,
                before: oldConfig,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpsertPluginConfig,
            // We delete all matching configs, but we only re-upsert
            // the first one in the anti-mutation.  There really
            // should only be a single matching config, because they
            // should all have `configID`s, plus the only plugins that
            // allow multiple configs right now are the SQL ones, so
            // this is ok(ish).
            data: { pluginConfig: oldConfig, before: null },
        },
    };
}

function applyUpdatePluginConfigParameters(
    oldDesc: AppDescription,
    mutation: UpdatePluginConfigParametersMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { pluginConfigID, pluginParams, before } = mutation.data;
    const pluginConfigs = oldDesc.pluginConfigs ?? [];
    const configIndex = pluginConfigs.findIndex(pc => pc.configID === pluginConfigID);
    if (configIndex < 0) return new ErrorInfo(404, "Plugin config not found");
    const oldConfig = defined(pluginConfigs[configIndex]);

    const result = updateShallow(oldConfig.parameters, respectCondition ? before : undefined, pluginParams);
    if (isErrorInfo(result) || result === undefined) return result;
    const { updated, antiBefore, antiUpdates } = result;

    return {
        newState: {
            ...oldDesc,
            pluginConfigs: replaceArrayItem(pluginConfigs, configIndex, {
                ...oldConfig,
                parameters: updated,
            }),
        },
        conditionalMutation: {
            kind: AppMutationKind.UpdatePluginConfigParameters,
            data: {
                ...mutation.data,
                before: antiUpdates as Record<string, PropertyDescription>,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdatePluginConfigParameters,
            data: {
                pluginConfigID,
                pluginParams: antiUpdates as Record<string, PropertyDescription>,
                before: antiBefore as Record<string, PropertyDescription>,
            },
        },
    };
}

function applyReorderTabs(
    oldDesc: AppDescription,
    mutation: ReorderTabsMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screenNames, before } = mutation.data;

    // We will remove tabs here as we go
    let oldTabs = getAppTabs(oldDesc);
    const oldScreenNames = mapFilterUndefined(oldTabs, t => getScreenProperty(t.screenName));
    if (respectCondition && before !== undefined && !deepEqual(oldScreenNames, before)) {
        return new ErrorInfo(412, "Tab order has changed");
    }

    const newTabs: TabDescription[] = [];

    for (const screenName of screenNames) {
        const tab = oldTabs.find(t => getScreenProperty(t.screenName) === screenName);
        if (tab === undefined) continue;

        newTabs.push(tab);
        oldTabs = oldTabs.filter(t => t !== tab);
    }

    // Add remaining tabs, if any
    newTabs.push(...oldTabs);

    // Check if the order actually changed
    const newScreenNames = mapFilterUndefined(newTabs, t => getScreenProperty(t.screenName));
    if (deepEqual(oldScreenNames, newScreenNames)) {
        return undefined; // No-op
    }

    return {
        newState: { ...oldDesc, tabs: newTabs },
        conditionalMutation: {
            kind: AppMutationKind.ReorderTabs,
            data: {
                ...mutation.data,
                before: oldScreenNames,
            },
        },
        antiMutation: {
            kind: AppMutationKind.ReorderTabs,
            data: {
                screenNames: oldScreenNames,
                before: mapFilterUndefined(newTabs, t => getScreenProperty(t.screenName)),
            },
        },
    };
}

function applyAddTabTemplate(
    oldDesc: AppDescription,
    mutation: AddTabTemplateMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screensToAdd, tabsToAdd } = mutation.data;

    const existingTabs = getAppTabs(oldDesc);

    for (const tab of tabsToAdd) {
        const screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) return new ErrorInfo(400, "Tab template has missing screen name");

        if (existingTabs.some(t => getScreenProperty(t.screenName) === screenName)) {
            return new ErrorInfo(400, "Tab template would overwrite existing tab");
        }
    }

    // Unfortunately `fixAppDescription` will already have added
    // default screens for the new tables, so we have to accept that
    // we're overwriting them here, instead of checking that they
    // don't exist.

    const tabs = [...existingTabs, ...tabsToAdd];

    const screenDescriptions = { ...oldDesc.screenDescriptions };
    for (const [screenName, screen] of Object.entries(screensToAdd)) {
        screenDescriptions[screenName] = screen;
    }

    return {
        newState: { ...oldDesc, tabs, screenDescriptions },
        conditionalMutation: mutation,
        antiMutation: {
            kind: AppMutationKind.RemoveTabTemplate,
            data: {
                tabsToRemove: tabsToAdd,
                screensToRemove: screensToAdd,
            },
        },
    };
}

function applyRemoveTabTemplate(
    oldDesc: AppDescription,
    mutation: RemoveTabTemplateMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { tabsToRemove, screensToRemove } = mutation.data;

    const existingTabs = getAppTabs(oldDesc);

    if (respectCondition) {
        for (const tab of tabsToRemove) {
            const screenName = getScreenProperty(tab.screenName);
            if (screenName === undefined) return new ErrorInfo(400, "Tab template has missing screen name");

            const existing = existingTabs.find(t => getScreenProperty(t.screenName) === screenName);
            if (existing === undefined) {
                // Deleting a non-existent tab is a no-op, so we'll
                // let it slide.
                continue;
            }

            if (!deepEqual(existing, tab)) return new ErrorInfo(412, "Tab has changed");
        }

        for (const [screenName, screen] of Object.entries(screensToRemove)) {
            const existing = oldDesc.screenDescriptions[screenName];
            if (existing === undefined) {
                // Deleting a non-existent screen is a no-op, so we'll
                // let it slide.
                continue;
            }

            if (!deepEqual(existing, screen)) return new ErrorInfo(412, "Screen has changed");
        }
    }

    const newTabs = existingTabs.filter(tab => {
        const screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) return true;
        return !tabsToRemove.some(t => getScreenProperty(t.screenName) === screenName);
    });

    const newScreenDescriptions = { ...oldDesc.screenDescriptions };
    for (const screenName of Object.keys(screensToRemove)) {
        delete newScreenDescriptions[screenName];
    }

    return {
        newState: { ...oldDesc, tabs: newTabs, screenDescriptions: newScreenDescriptions },
        conditionalMutation: mutation,
        antiMutation: {
            kind: AppMutationKind.AddTabTemplate,
            data: {
                tabsToAdd: tabsToRemove,
                screensToAdd: screensToRemove,
            },
        },
    };
}

function applyUpdateScreenDescription(
    oldDesc: AppDescription,
    mutation: UpdateScreenDescriptionMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screenName, updates, allowNewScreen, before } = mutation.data;

    const oldScreenDesc = oldDesc.screenDescriptions[screenName];
    if (oldScreenDesc === undefined) {
        if (updates === false) return undefined;
        if (!allowNewScreen) return new ErrorInfo(404, "Screen doesn't exist");
        if (respectCondition && before !== undefined && before !== null) {
            return new ErrorInfo(412, "Screen doesn't exist");
        }
        return {
            newState: {
                ...oldDesc,
                screenDescriptions: {
                    ...oldDesc.screenDescriptions,
                    [screenName]: updates as ScreenDescription,
                },
            },
            conditionalMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    ...mutation.data,
                    before: null,
                },
            },
            antiMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    screenName,
                    updates: false,
                    allowNewScreen: false,
                    before: updates,
                },
            },
        };
    } else if (updates === false) {
        if (respectCondition && before !== undefined && !deepEqual(oldScreenDesc, before)) {
            return new ErrorInfo(412, "Screen has changed");
        }
        const newScreenDescriptions = { ...oldDesc.screenDescriptions };
        delete newScreenDescriptions[screenName];
        return {
            newState: {
                ...oldDesc,
                screenDescriptions: newScreenDescriptions,
            },
            conditionalMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    ...mutation.data,
                    before: oldScreenDesc,
                },
            },
            antiMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    screenName,
                    updates: oldScreenDesc,
                    allowNewScreen: true,
                    before: null,
                },
            },
        };
    } else {
        if (respectCondition && before === null) return new ErrorInfo(412, "Screen exists");
        const result = updateShallow(oldScreenDesc, respectCondition ? nonNull(before) : undefined, updates);
        if (isErrorInfo(result) || result === undefined) return result;
        const { updated, antiBefore, antiUpdates } = result;
        return {
            newState: {
                ...oldDesc,
                screenDescriptions: {
                    ...oldDesc.screenDescriptions,
                    [screenName]: updated as unknown as ScreenDescription,
                },
            },
            conditionalMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    ...mutation.data,
                    before: antiUpdates,
                },
            },
            antiMutation: {
                kind: AppMutationKind.UpdateScreenDescription,
                data: {
                    screenName,
                    updates: antiUpdates,
                    allowNewScreen: false,
                    before: antiBefore,
                },
            },
        };
    }
}

function applyUpdateComponentDescription(
    oldDesc: AppDescription,
    mutation: UpdateComponentDescriptionMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { screenName, componentID, newDescription, before } = mutation.data;

    const oldScreenDesc = oldDesc.screenDescriptions[screenName];
    if (!isClassOrArrayScreenDescription(oldScreenDesc)) return new ErrorInfo(400, "Screen is invalid");

    const components = getScreenComponents(oldScreenDesc);
    const componentIndexes = findComponentIndexByID(oldScreenDesc, componentID);
    if (componentIndexes === undefined) return new ErrorInfo(404, "Component not found in screen");

    let oldComponent: ComponentDescription | undefined;
    let newComponents: ComponentDescription[];
    if (componentIndexes.length === 1) {
        oldComponent = defined(components[componentIndexes[0]]);
        newComponents = replaceArrayItem(components, componentIndexes[0], newDescription);
    } else if (componentIndexes.length === 2) {
        const parent = defined(components[componentIndexes[0]]);
        const subComponents = defined(getSubComponents(parent));
        oldComponent = defined(subComponents[componentIndexes[1]]);
        const newSubComponents = replaceArrayItem(subComponents, componentIndexes[1], newDescription);
        const newParent = { ...parent, components: newSubComponents };
        newComponents = replaceArrayItem(components, componentIndexes[0], newParent);
    } else {
        return assertNever(componentIndexes);
    }

    if (deepEqual(oldComponent, newDescription)) {
        // This is a no-op
        return undefined;
    }

    if (respectCondition && before !== undefined && !deepEqual(oldComponent, before)) {
        return new ErrorInfo(412, "Component has changed");
    }

    return {
        newState: {
            ...oldDesc,
            screenDescriptions: {
                ...oldDesc.screenDescriptions,
                [screenName]: { ...oldScreenDesc, components: newComponents },
            },
        },
        conditionalMutation: {
            kind: AppMutationKind.UpdateComponentDescription,
            data: {
                ...mutation.data,
                before: oldComponent,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdateComponentDescription,
            data: {
                screenName,
                componentID,
                newDescription: oldComponent,
                before: newDescription,
            },
        },
    };
}

function applyUpdateTheme(
    oldDesc: AppDescription,
    mutation: UpdateThemeMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { updates, before } = mutation.data;
    const result = updateShallow(oldDesc.theme, respectCondition ? before : undefined, updates);
    if (isErrorInfo(result) || result === undefined) return result;
    const { updated, antiBefore, antiUpdates } = result;
    return {
        newState: { ...oldDesc, theme: updated },
        conditionalMutation: {
            kind: AppMutationKind.UpdateTheme,
            data: {
                ...mutation.data,
                before: antiUpdates,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdateTheme,
            data: { updates: antiUpdates, before: antiBefore },
        },
    };
}

function applyUpdateFeatures(
    oldDesc: AppDescription,
    mutation: UpdateFeaturesMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { updates, before } = mutation.data;
    const result = updateShallow(oldDesc.features ?? {}, respectCondition ? before : undefined, updates);
    if (isErrorInfo(result) || result === undefined) return result;
    const { updated, antiBefore, antiUpdates } = result;
    return {
        newState: { ...oldDesc, features: updated },
        conditionalMutation: {
            kind: AppMutationKind.UpdateFeatures,
            data: {
                ...mutation.data,
                before: antiUpdates,
            },
        },
        antiMutation: {
            kind: AppMutationKind.UpdateFeatures,
            data: { updates: antiUpdates, before: antiBefore },
        },
    };
}

function applyUpsertSourceMetadata(
    oldDesc: AppDescription,
    mutation: AddSourceMetadataMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { sourceMetadata } = mutation.data;
    const oldSourceMetadataArray = getSourceMetadata(oldDesc);
    const oldSourceMetadataIndex = oldSourceMetadataArray.findIndex(
        sm => sourceMetadata.id === sm.id && sourceMetadata.type === sm.type
    );
    let newSourceMetadataArray: SourceMetadata[];
    if (oldSourceMetadataIndex >= 0) {
        newSourceMetadataArray = replaceArrayItem(oldSourceMetadataArray, oldSourceMetadataIndex, sourceMetadata);
    } else {
        newSourceMetadataArray = [sourceMetadata, ...oldSourceMetadataArray];
    }

    return {
        newState: {
            ...oldDesc,
            sourceMetadataArray: newSourceMetadataArray,
        },
        conditionalMutation: mutation,
        antiMutation: undefined,
    };
}

function applyUpsertGoogleSheetSourceMetadata(
    oldDesc: AppDescription,
    mutation: AddOrReplaceGoogleSheetSourceMetadataMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { sourceMetadata } = mutation.data;
    return {
        newState: {
            ...oldDesc,
            sourceMetadataArray: [
                ...getSourceMetadata(oldDesc).filter(sm => sm.type !== "Google Sheet"),
                sourceMetadata,
            ],
            sourceMetadata: undefined,
        },
        conditionalMutation: mutation,
        antiMutation: undefined,
    };
}

function applyRemoveSourceMetadata(
    oldDesc: AppDescription,
    mutation: RemoveSourceMetadataMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { sourceMetadata } = mutation.data;
    return {
        newState: {
            ...oldDesc,
            sourceMetadataArray: getSourceMetadata(oldDesc).filter(sm => !areSourceMetadatasEqual(sm, sourceMetadata)),
        },
        conditionalMutation: mutation,
        antiMutation: undefined,
    };
}

function applyApplyUndo(
    _oldDesc: AppDescription,
    mutation: ApplyUndoMutation,
    _respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    const { appDescription } = mutation.data;
    return {
        newState: appDescription,
        conditionalMutation: mutation,
        antiMutation: undefined,
    };
}

/**
 * Returns an error if
 * - the mutation is invalid in the current state, or
 * - if the mutation's condition does not allow it (if `respectCondition` is
 *   `true`), or
 *
 * Returns `undefined` if
 * - it's a no-op
 */
export function applyMutationFull(
    oldDesc: AppDescription,
    mutation: AppDescriptionMutation,
    respectCondition: boolean
): ErrorOr<MutationResult | undefined> {
    switch (mutation.kind) {
        case AppMutationKind.UpdateTabDescription:
            return applyUpdateTabDescription(oldDesc, mutation, respectCondition);
        case AppMutationKind.RemoveTab:
            return applyRemoveTab(oldDesc, mutation, respectCondition);
        case AppMutationKind.ReorderComponent:
            return applyReorderComponent(oldDesc, mutation, respectCondition);
        case AppMutationKind.AddTab:
            return applyAddTab(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdateSerializedApp:
            return applyUpdateSerializedApp(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpsertPluginConfig:
            return applyUpsertPluginConfig(oldDesc, mutation, respectCondition);
        case AppMutationKind.DeletePluginConfig:
            return applyDeletePluginConfig(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdatePluginConfigParameters:
            return applyUpdatePluginConfigParameters(oldDesc, mutation, respectCondition);
        case AppMutationKind.ReorderTabs:
            return applyReorderTabs(oldDesc, mutation, respectCondition);
        case AppMutationKind.AddTabTemplate:
            return applyAddTabTemplate(oldDesc, mutation, respectCondition);
        case AppMutationKind.RemoveTabTemplate:
            return applyRemoveTabTemplate(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdateScreenDescription:
            return applyUpdateScreenDescription(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdateComponentDescription:
            return applyUpdateComponentDescription(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdateTheme:
            return applyUpdateTheme(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpdateFeatures:
            return applyUpdateFeatures(oldDesc, mutation, respectCondition);
        // The source metadata mutations don't have anti-mutations because
        // they are the results of schema mutations which right now are not
        // undoable.
        case AppMutationKind.UpsertSourceMetadata:
            return applyUpsertSourceMetadata(oldDesc, mutation, respectCondition);
        case AppMutationKind.UpsertGoogleSheetSourceMetadata:
            return applyUpsertGoogleSheetSourceMetadata(oldDesc, mutation, respectCondition);
        case AppMutationKind.RemoveSourceMetadata:
            return applyRemoveSourceMetadata(oldDesc, mutation, respectCondition);
        case AppMutationKind.ApplyUndo:
            return applyApplyUndo(oldDesc, mutation, respectCondition);
        default:
            assertNever(mutation);
    }
}

export function applyMutation(oldDesc: AppDescription, mutation: AppDescriptionMutation): AppDescription {
    const result = applyMutationFull(oldDesc, mutation, false);
    if (isErrorInfo(result)) return oldDesc;
    return result?.newState ?? oldDesc;
}
